import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import { selectLoanContactsAllowedActions } from 'store/loanApplications/selectors';

import { ENTITY_TYPE_HREF_MAP } from '../../propertyAndLoan/EditModal/EditModal.hrefs';
import { EDIT_CONTACT_MENU_LABEL, REMOVE_CONTACT_MENU_LABEL } from '../Parties.constants';
import { PartiesTestIds } from '../Parties.testIds';

const PartyActionButton = ({ row, isDisabled = false, isContactLocked = false, onRemove }) => {
  const { applicationId, tab } = useParams();
  const history = useHistory();

  const { isEditAllowed, isManageAllowed } = useSelector(selectLoanContactsAllowedActions);

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const { contactId, editLocation, isPendingRemoval } = row;

  const canChangeContact = !!contactId && !isPendingRemoval && !isContactLocked;

  const onEditContact = useCallback(() => {
    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: ENTITY_TYPE_HREF_MAP.CONTACTS,
        entityId: contactId,
      }),
    );
  }, [applicationId, contactId, history, tab]);

  const onRemoveContact = useCallback(() => onRemove(row), [onRemove, row]);

  const onOpenEditLocation = useCallback(() => {
    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        ...editLocation.pathParams,
      }),
    );
  }, [applicationId, editLocation, history]);

  const items = useMemo(
    () => [
      ...getConditionalArray(canChangeContact && isEditAllowed, [
        {
          text: EDIT_CONTACT_MENU_LABEL,
          onClick: onEditContact,
          icon: <EditPenIcon size={IconSize[20]} />,
        },
      ]),
      ...getConditionalArray(canChangeContact && isManageAllowed, [
        {
          text: REMOVE_CONTACT_MENU_LABEL,
          onClick: onRemoveContact,
          icon: <DeleteIcon size={IconSize[20]} />,
        },
      ]),
      ...getConditionalArray(!!editLocation, () => [
        {
          text: editLocation.label,
          onClick: onOpenEditLocation,
          icon: <EditPenIcon size={IconSize[20]} />,
        },
      ]),
    ],
    [
      canChangeContact,
      isEditAllowed,
      isManageAllowed,
      editLocation,
      onEditContact,
      onRemoveContact,
      onOpenEditLocation,
    ],
  );

  return (
    <>
      <MoreButtonCDS
        data-testid={PartiesTestIds.ROW_ACTION_BUTTON}
        disabled={!items.length || isDisabled}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS
        anchorEl={anchorEl}
        data-testid={PartiesTestIds.ROW_ACTION_MENU}
        items={items}
        onClick={onClearAnchorElement}
        onClose={onClearAnchorElement}
      />
    </>
  );
};

PartyActionButton.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
    contactId: PropTypes.string,
    isPendingRemoval: PropTypes.bool,
    editLocation: PropTypes.shape({
      label: PropTypes.string.isRequired,
      pathParams: PropTypes.object.isRequired,
    }),
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  isContactLocked: PropTypes.bool,
};

export default PartyActionButton;
